import { ChevronDown, ChevronTop, Pencil, Plus } from 'assets/icons';
import Button from 'components/button';
import UseModal from 'components/modal/useModal';
import Table from 'components/table/table';
import UseContext from 'hooks/useContext';
import useCardFlash, { FLASH_CLASS } from 'hooks/useCardFlash';
import LastUpdateIcon from 'components/lastUpdateIcon/LastUpdateIcon';
import { mostRecent } from 'helper/lastUpdate';
import { useEffect, useState } from 'react';

const SlideOverCompanyBonusAndCompensation = ({
  companyInfo,
  setCompanyInfo,
  onSaved,
}) => {
  const { toggle } = UseModal();
  const { setModalData, modalDataReturn, setModalDataReturn } = UseContext();
  const [isShowBonusesAndAllowances, setIsShowBonusesAndAllowances] =
    useState(false);
  const { flashCard, isFlashed, registerCard } = useCardFlash();
  const token = localStorage.getItem('xsrfToken');

  const restaurantTicket =
    companyInfo?.bonusesAndAllowances?.restaurant_ticket ?? {};

  const formatEuros = (amount) =>
    `${Number(amount).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;

  const editBonusesAndAllowances = (idCompany) => {
    // The names travel with the modal so it can warn about a rubric already
    // configured here. Names rather than ids: that is what the endpoint
    // resolves them to before they reach this page.
    setModalData({
      idCompany,
      configuredBonusNames: (
        companyInfo?.bonusesAndAllowances?.bonuses ?? []
      ).map((bonus) => bonus.name),
    });
    toggle('edit_bonuses_and_allowances');
  };

  const editBonusesAndAllowancesRestaurantTicket = (idCompany) => {
    setModalData({ idCompany });
    toggle('edit_bonuses_and_allowances_restaurant_ticket');
  };

  useEffect(() => {
    if (
      modalDataReturn?.type === 'restaurant_ticket_updated' &&
      modalDataReturn?.restaurantTicket
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        bonusesAndAllowances: {
          ...prevData.bonusesAndAllowances,
          restaurant_ticket: modalDataReturn.restaurantTicket,
        },
      }));
      flashCard('restaurantTicket');
      // The optimistic update above keeps the card instant; this refetch is
      // what fills in lastUpdates, which the save endpoint does not return.
      onSaved?.();
    }
    if (
      modalDataReturn?.type === 'bonuses_and_allowances_added' &&
      modalDataReturn?.bonusesAndAllowances
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        bonusesAndAllowances: {
          ...prevData.bonusesAndAllowances,
          bonuses: [
            ...(prevData.bonusesAndAllowances?.bonuses || []),
            modalDataReturn.bonusesAndAllowances,
          ],
        },
      }));
      setIsShowBonusesAndAllowances(true);
      flashCard('bonuses');
    }
    if (
      modalDataReturn?.type === 'bonuses_and_allowances_updated' &&
      modalDataReturn?.bonusesAndAllowances
    ) {
      setCompanyInfo((prevData) => {
        const updatedBonuses = prevData.bonusesAndAllowances?.bonuses?.map(
          (bonus) =>
            bonus._id.toString() ===
            modalDataReturn.bonusesAndAllowances._id.toString()
              ? modalDataReturn.bonusesAndAllowances
              : bonus
        );

        return {
          ...prevData,
          bonusesAndAllowances: {
            ...prevData.bonusesAndAllowances,
            bonuses: updatedBonuses,
          },
        };
      });
    }
    if (
      modalDataReturn?.type === 'bonuses_and_allowances_deleted' &&
      modalDataReturn?.idBonusesAndAllowances
    ) {
      setCompanyInfo((prevData) => {
        const updatedBonuses = prevData.bonusesAndAllowances?.bonuses?.filter(
          (bonus) =>
            bonus._id.toString() !==
            modalDataReturn.idBonusesAndAllowances.toString()
        );

        if (updatedBonuses.length === 0) setIsShowBonusesAndAllowances(false);
        else setIsShowBonusesAndAllowances(true);

        return {
          ...prevData,
          bonusesAndAllowances: {
            ...prevData.bonusesAndAllowances,
            bonuses: updatedBonuses,
          },
        };
      });
    }

    setModalDataReturn(null);
  }, [modalDataReturn]);

  // No longer « payé x coef = facturé »: the amount is a majoration rate, and
  // both the coefficient and the billed amount are resolved from the mission.
  const titles = [
    'AUTRES PRIMES ET RUBRIQUES',
    'UNITÉS',
    'MAJORATION',
    'COEFFICIENT',
  ];

  useEffect(() => {
    if (companyInfo?.bonusesAndAllowances?.bonuses?.length > 0)
      setIsShowBonusesAndAllowances(true);
  }, [companyInfo?.bonusesAndAllowances?.bonuses?.length]);

  return (
    <>
      <div
        ref={registerCard('bonuses')}
        className={`border rounded p-3 flex flex-col gap-2.5 ${
          isFlashed('bonuses') ? FLASH_CLASS : ''
        }`}
      >
        {/* Only the header adds a line — the body is a table whose own rows
            open the line they carry, so the card as a whole cannot be one
            target. */}
        <div
          role={'button'}
          tabIndex={0}
          className="flex justify-between items-center gap-2 cursor-pointer"
          onClick={() => editBonusesAndAllowances(companyInfo?._id)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            // Space would scroll the panel the card sits in.
            event.preventDefault();
            editBonusesAndAllowances(companyInfo?._id);
          }}
        >
          <div className={''}>
            <div className={''}>Primes & indemnité</div>
          </div>
          <div className={'flex items-center gap-2'}>
            {/* Adding, editing and deleting a line each change the list, so
                the clock dates whichever happened last. */}
            <LastUpdateIcon
              lastUpdate={mostRecent(
                companyInfo?.lastUpdates?.modification_prime_indemnite,
                companyInfo?.lastUpdates?.ajout_prime_indemnite,
                companyInfo?.lastUpdates?.suppression_prime_indemnite
              )}
            />
            <Button
              size={'SMI'}
              // SECONDARY like every edit button on the page: ICON dropped the
              // border and the hover, so these two read as a different control
              // from the ones on the sections above.
              color={'SECONDARY'}
              // The header behind fires the same action; without this the click
              // would run it twice, opening and closing the modal in one go.
              onClick={(event) => {
                event.stopPropagation();
                editBonusesAndAllowances(companyInfo?._id);
              }}
            >
              <Plus wh={20} color={'#111827'} />
            </Button>
            {companyInfo?.bonusesAndAllowances?.bonuses?.length > 0 && (
              <Button
                size={'SMI'}
                color={'SECONDARY'}
                onClick={(event) => {
                  // Folding the list is not adding to it.
                  event.stopPropagation();
                  setIsShowBonusesAndAllowances(!isShowBonusesAndAllowances);
                }}
              >
                {isShowBonusesAndAllowances ? (
                  <ChevronDown wh={20} color={'#111827'} />
                ) : (
                  <ChevronTop wh={20} color={'#111827'} />
                )}
              </Button>
            )}
          </div>
        </div>
        {isShowBonusesAndAllowances && (
          <div>
            <Table
              champsBody={{
                bonuses: companyInfo?.bonusesAndAllowances.bonuses,
                companyInfo,
              }}
              champsHeader={titles}
              type={'companyBonusAndCompensation'}
            />
          </div>
        )}
      </div>
      {/* One datum, one action: the card opens the modal like the sections
          above it. Primes & indemnité stays untouched — its body is a table
          with its own rows, and its header carries a second control. */}
      <div
        ref={registerCard('restaurantTicket')}
        role={'button'}
        tabIndex={0}
        className={`border rounded p-3 flex flex-col gap-2.5 cursor-pointer ${
          isFlashed('restaurantTicket') ? FLASH_CLASS : 'hover:bg-gray-50'
        }`}
        onClick={() =>
          editBonusesAndAllowancesRestaurantTicket(companyInfo?._id)
        }
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          // Space would scroll the panel the card sits in.
          event.preventDefault();
          editBonusesAndAllowancesRestaurantTicket(companyInfo?._id);
        }}
      >
        <div className="flex justify-between items-center gap-2">
          <div className={''}>
            <div className={''}>Ticket restaurant</div>
            {restaurantTicket.employer_share && restaurantTicket.modality && (
              <div className={'text-gray-500'}>
                {/* A ticket saved before the value existed keeps its old
                    wording rather than showing an empty amount. */}
                {restaurantTicket.value ? (
                  <div>
                    Ticket de {formatEuros(restaurantTicket.value)} (
                    {restaurantTicket.employer_share} % part patronale)
                  </div>
                ) : (
                  <div>
                    La part patronale est de {restaurantTicket.employer_share}%
                  </div>
                )}
                <div>{restaurantTicket.modality}</div>
              </div>
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {/* Falls back to the "ajout_" log while the ticket has only ever
                been created. */}
            <LastUpdateIcon
              lastUpdate={
                companyInfo?.lastUpdates?.modification_ticket_restaurant ??
                companyInfo?.lastUpdates?.ajout_ticket_restaurant
              }
            />
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={(event) => {
                // The card behind fires the same action; without this the
                // click would open and close the modal in one go.
                event.stopPropagation();
                editBonusesAndAllowancesRestaurantTicket(companyInfo?._id);
              }}
            >
              {restaurantTicket.employer_share &&
              restaurantTicket.modality ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlideOverCompanyBonusAndCompensation;
